import { useCallback, useEffect, useRef } from "react"
import { useDebounce } from "react-use"

const SAVE_DELAY_MS = 700

// A whitespace-only draft persists as empty, so it compares as unchanged.
const normalize = (value: string) => (value.trim().length > 0 ? value : "")

// Mobile browsers discard a backgrounded tab without ever firing blur, so the
// visibility flush is what keeps an in-progress edit.
export function useAutosaveText({
	value,
	persistedValue,
	onSave,
	enabled = true,
}: {
	value: string
	persistedValue: string
	onSave: (value: string) => void | Promise<unknown>
	enabled?: boolean
}) {
	const latest = useRef({ value, persistedValue, onSave, enabled })
	latest.current = { value, persistedValue, onSave, enabled }

	const flush = useCallback((override?: string) => {
		const current = latest.current
		if (!current.enabled) return
		const next = normalize(override ?? current.value)
		if (next === normalize(current.persistedValue)) return
		void current.onSave(next)
	}, [])

	useDebounce(() => flush(), SAVE_DELAY_MS, [value, enabled])

	useEffect(() => {
		const onVisibilityChange = () => {
			if (document.visibilityState === "hidden") flush()
		}
		const onPageHide = () => flush()
		document.addEventListener("visibilitychange", onVisibilityChange)
		window.addEventListener("pagehide", onPageHide)
		return () => {
			document.removeEventListener("visibilitychange", onVisibilityChange)
			window.removeEventListener("pagehide", onPageHide)
			flush()
		}
	}, [flush])

	return flush
}
